import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Bell, CheckCheck, Ticket, MessageSquare, UserPlus, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../../lib/utils';

const notificationIcons = {
  new_ticket: Ticket,
  new_message: MessageSquare,
  assigned: UserPlus,
  status_change: AlertCircle,
  default: Bell
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { notifications, markAsRead, markAllAsRead } = useNotifications();

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.ticket_id) {
      navigate(`/backoffice/tickets/${notification.ticket_id}`);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div data-testid="notification-center" className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Notifications</h1>
          <p className="text-slate-600 dark:text-zinc-400 mt-1">
            {unreadCount} notification{unreadCount > 1 ? 's' : ''} non lue{unreadCount > 1 ? 's' : ''}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={markAllAsRead}>
            <CheckCheck className="w-4 h-4 mr-2" />
            Tout marquer comme lu
          </Button>
        )}
      </div>

      <Card className="border-slate-200 dark:border-zinc-800">
        <CardContent className="p-0 divide-y divide-slate-200 dark:divide-zinc-800">
          {notifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-12 h-12 text-slate-300 dark:text-zinc-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                Aucune notification
              </h3>
              <p className="text-slate-600 dark:text-zinc-400">
                Vous n'avez pas encore de notifications.
              </p>
            </div>
          ) : (
            notifications.map((notification) => {
              const IconComponent = notificationIcons[notification.type] || notificationIcons.default;
              
              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full flex items-start gap-4 p-4 text-left hover:bg-slate-50 dark:hover:bg-zinc-900 transition-colors",
                    !notification.read && "bg-emerald-50/50 dark:bg-emerald-900/10"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                    notification.read 
                      ? "bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400"
                      : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                  )}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "font-medium",
                      notification.read 
                        ? "text-slate-700 dark:text-zinc-300"
                        : "text-slate-900 dark:text-white"
                    )}>
                      {notification.title}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-zinc-400 mt-0.5">
                      {notification.message}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-zinc-500 mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: fr })}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-2" />
                  )}
                </button>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
